import type * as Types from 'app/types/graphql';

import {
  CampaignWithVariantTemplateEdgeSelectFragmentDoc,
  CampaignWithVariantTemplateSelectFragmentDoc,
} from '../fragments/campaignTemplate.generated';
import {
  CampaignSelectFragmentDoc,
  CampaignVersionSelectFragmentDoc,
} from '../fragments/campaign.generated';
import {
  VariantWithTemplateSelectFragmentDoc,
  VariantSelectFragmentDoc,
} from '../fragments/variant.generated';
import { VariantTemplateSelectFragmentDoc } from '../fragments/variantTemplate.generated';
import { VariantPriceSelectFragmentDoc } from '../fragments/variantPrice.generated';
import { VariantProductVariantSelectFragmentDoc } from '../fragments/variantProductVariant.generated';
import { VariantUrlSelectFragmentDoc } from '../fragments/variantUrl.generated';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { appFetcher } from '~/api/utils/gatewayClient';
export type CampaignTemplatesQueryVariables = Types.Exact<{
  after?: Types.InputMaybe<Types.Scalars['Cursor']['input']>;
  before?: Types.InputMaybe<Types.Scalars['Cursor']['input']>;
  first?: Types.InputMaybe<Types.Scalars['Int']['input']>;
  last?: Types.InputMaybe<Types.Scalars['Int']['input']>;
  orderBy?: Types.InputMaybe<Types.CampaignOrder>;
  where?: Types.InputMaybe<Types.CampaignWhereInput>;
}>;

export type CampaignTemplatesQueryResponse = {
  campaigns?: Types.Maybe<
    Pick<Types.CampaignConnection, 'totalCount'> & {
      edges?: Types.Maybe<
        Array<
          Types.Maybe<
            Pick<Types.CampaignEdge, 'cursor'> & {
              node?: Types.Maybe<
                Pick<
                  Types.Campaign,
                  | 'id'
                  | 'name'
                  | 'type'
                  | 'status'
                  | 'priority'
                  | 'storeDomain'
                  | 'targetDevices'
                  | 'targetChannels'
                  | 'targetCountries'
                  | 'targetVisitorTypes'
                  | 'conditions'
                  | 'createdAt'
                  | 'updatedAt'
                  | 'startedAt'
                  | 'deletedAt'
                  | 'completedAt'
                > & {
                  variants?: Types.Maybe<
                    Array<
                      Types.Maybe<
                        Pick<
                          Types.Variant,
                          | 'id'
                          | 'isOrigin'
                          | 'isWinner'
                          | 'trafficRate'
                          | 'themePageID'
                          | 'templateType'
                          | 'templateSuffix'
                          | 'createdAt'
                          | 'deletedAt'
                          | 'updatedAt'
                          | 'winnerTemplateSuffix'
                        > & {
                          template?: Types.Maybe<
                            Pick<
                              Types.VariantTemplate,
                              | 'createdAt'
                              | 'deletedAt'
                              | 'id'
                              | 'redirectURL'
                              | 'themeID'
                              | 'updatedAt'
                              | 'thumbnailURL'
                              | 'title'
                            >
                          >;
                          price?: Types.Maybe<
                            Pick<
                              Types.VariantPrice,
                              | 'id'
                              | 'baseID'
                              | 'handle'
                              | 'status'
                              | 'title'
                              | 'redirectURL'
                              | 'sourceProductBaseID'
                            >
                          >;
                          productVariants?: Types.Maybe<
                            Array<
                              Types.Maybe<
                                Pick<
                                  Types.VariantProductVariant,
                                  | 'id'
                                  | 'baseID'
                                  | 'name'
                                  | 'price'
                                  | 'compareAtPrice'
                                  | 'variantID'
                                >
                              >
                            >
                          >;
                          url?: Types.Maybe<
                            Pick<
                              Types.VariantUrl,
                              'id' | 'baseID' | 'handle' | 'title' | 'redirectURL' | 'resourceType'
                            >
                          >;
                        }
                      >
                    >
                  >;
                  group?: Types.Maybe<Pick<Types.CampaignGroup, 'id' | 'title' | 'type'>>;
                  versions?: Types.Maybe<
                    Array<
                      Types.Maybe<
                        Pick<
                          Types.SplitTestingCampaignVersion,
                          'id' | 'version' | 'settings' | 'startedAt' | 'completedAt'
                        >
                      >
                    >
                  >;
                }
              >;
            }
          >
        >
      >;
      pageInfo: Pick<
        Types.PageInfo,
        'endCursor' | 'hasNextPage' | 'hasPreviousPage' | 'startCursor'
      >;
    }
  >;
};

export const CampaignTemplatesDocument = `
    query CampaignTemplates($after: Cursor, $before: Cursor, $first: Int, $last: Int, $orderBy: CampaignOrder, $where: CampaignWhereInput) {
  campaigns(
    after: $after
    before: $before
    first: $first
    last: $last
    orderBy: $orderBy
    where: $where
  ) {
    edges {
      ...CampaignWithVariantTemplateEdgeSelect
    }
    pageInfo {
      endCursor
      hasNextPage
      hasPreviousPage
      startCursor
    }
    totalCount
  }
}
    ${CampaignWithVariantTemplateEdgeSelectFragmentDoc}
${CampaignWithVariantTemplateSelectFragmentDoc}
${CampaignSelectFragmentDoc}
${CampaignVersionSelectFragmentDoc}
${VariantWithTemplateSelectFragmentDoc}
${VariantSelectFragmentDoc}
${VariantTemplateSelectFragmentDoc}
${VariantPriceSelectFragmentDoc}
${VariantProductVariantSelectFragmentDoc}
${VariantUrlSelectFragmentDoc}`;

export const useCampaignTemplatesQuery = <TData = CampaignTemplatesQueryResponse, TError = unknown>(
  variables?: CampaignTemplatesQueryVariables,
  options?: UseQueryOptions<CampaignTemplatesQueryResponse, TError, TData>,
) => {
  return useQuery<CampaignTemplatesQueryResponse, TError, TData>(
    variables === undefined ? ['CampaignTemplates'] : ['CampaignTemplates', variables],
    appFetcher<CampaignTemplatesQueryResponse, CampaignTemplatesQueryVariables>(
      CampaignTemplatesDocument,
      variables,
    ),
    options,
  );
};

useCampaignTemplatesQuery.getKey = (variables?: CampaignTemplatesQueryVariables) =>
  variables === undefined ? ['CampaignTemplates'] : ['CampaignTemplates', variables];
